import { useCallback, useEffect, useRef, useState } from 'react';
import type { AppSession, PracticeView, ThreadSummary, ThreadView } from '../../lib/types';
import { ApiError, api } from '../../lib/api';
import { fmtAgo, fmtDateTime } from '../../lib/format';
import { patientHash, threadHash } from '../../lib/routes';
import { Icon } from '../../components/Mark';
import { Avatar, Card, Empty } from '../../components/Bits';

/** The doctor's inbox: every patient conversation, unread first, and the open one with a reply box. */
export function MessagesPage({ session, practice, patientId, onChanged, say }: { session: AppSession; practice: PracticeView; patientId: string | null; onChanged: () => Promise<void>; say: (s: string) => void }) {
  const [threads, setThreads] = useState<ThreadSummary[] | null>(null);
  const [thread, setThread] = useState<ThreadView | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const chatRef = useRef<HTMLDivElement | null>(null);

  const loadThreads = useCallback(async () => {
    try {
      setThreads((await api.threads(session.token)).threads);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not load messages.');
    }
  }, [session.token]);

  const loadThread = useCallback(async () => {
    if (!patientId) {
      setThread(null);
      return;
    }
    try {
      setThread(await api.thread(patientId, session.token));
      setError(null);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not open that conversation.');
    }
  }, [patientId, session.token]);

  useEffect(() => {
    void loadThreads();
    const h = setInterval(() => {
      if (!document.hidden) void Promise.all([loadThreads(), loadThread()]);
    }, 20_000);
    return () => clearInterval(h);
  }, [loadThreads, loadThread]);

  useEffect(() => {
    // Opening a conversation marks it read, so the list and the sidebar count refresh after it.
    void loadThread().then(() => Promise.all([loadThreads(), onChanged()]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadThread]);

  useEffect(() => {
    const el = chatRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [thread]);

  const send = async () => {
    const t = text.trim();
    if (!t || !patientId) return;
    setBusy(true);
    try {
      await api.sendMessage(patientId, t, session.token);
      setText('');
      await Promise.all([loadThread(), loadThreads()]);
    } catch (e) {
      say(e instanceof ApiError ? e.message : 'Could not send that.');
    } finally {
      setBusy(false);
    }
  };

  const first = thread?.patientName.split(' ')[0] ?? '';

  return (
    <>
      <header className="desk-head">
        <div>
          <h1>Messages</h1>
          <span className="hint">What your patients at {practice.name} have written to the care team</span>
        </div>
        <select
          value=""
          aria-label="Message a patient"
          onChange={(e) => {
            if (e.target.value) location.hash = threadHash(e.target.value);
          }}
        >
          <option value="">Message a patient…</option>
          {[...practice.patients]
            .sort((a, b) => a.name.localeCompare(b.name))
            .map((p) => (
              <option key={p.patientId} value={p.patientId}>
                {p.name}
              </option>
            ))}
        </select>
      </header>
      {error ? <p className="form-error">{error}</p> : null}
      <div className="messages-layout">
        <Card className="table-card">
          {threads === null ? <p className="hint card-pad">Loading…</p> : null}
          {threads !== null && threads.length === 0 ? <Empty title="No messages yet">When a patient writes to the care team, it lands here.</Empty> : null}
          <ul className="threads">
            {(threads ?? []).map((t) => (
              <li key={t.patientId}>
                <a className={`thread-item ${t.patientId === patientId ? 'on' : ''} ${t.unread > 0 ? 'unread' : ''}`} href={threadHash(t.patientId)}>
                  <Avatar name={t.patientName} size="sm" />
                  <span className="row-main">
                    <strong>{t.patientName}</strong>
                    <span className="hint">
                      {t.lastFrom === 'patient' ? '' : 'You: '}
                      {t.lastText}
                    </span>
                  </span>
                  <span className="thread-side">
                    <span className="hint small">{fmtAgo(t.lastAt)}</span>
                    {t.unread > 0 ? <span className="badge badge-red">{t.unread} new</span> : null}
                  </span>
                </a>
              </li>
            ))}
          </ul>
        </Card>

        {patientId && thread ? (
          <section className="card chat-card">
            <header className="chat-head">
              <Avatar name={thread.patientName} size="sm" />
              <div className="row-main">
                <h2>{thread.patientName}</h2>
                <span className="hint light small">Patient of {thread.doctorName}</span>
              </div>
              <a className="button secondary small" href={patientHash(patientId)}>
                Open chart
              </a>
            </header>
            <div className="chat" ref={chatRef}>
              {thread.messages.length === 0 ? <p className="hint center">No messages with {first} yet. Write the first one below.</p> : null}
              {thread.messages.map((m) => {
                const mine = m.from.role !== 'patient';
                return (
                  <div key={m.id} className={`bubble-msg ${mine ? 'mine' : ''}`}>
                    <div className="msg">
                      {mine ? <strong className="msg-from">{m.from.name}</strong> : null}
                      <p>{m.text}</p>
                      <span className="msg-meta">
                        {m.kind === 'reminder' ? 'Reminder · ' : ''}
                        {m.deliveredAt ? fmtAgo(m.deliveredAt) : m.scheduledAt ? `scheduled for ${fmtDateTime(m.scheduledAt)}` : 'sending'}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
            <form
              className="composer"
              onSubmit={(e) => {
                e.preventDefault();
                void send();
              }}
            >
              <input type="text" value={text} maxLength={1000} placeholder={`Reply to ${first}…`} aria-label={`Reply to ${first}`} onChange={(e) => setText(e.target.value)} />
              <button type="submit" className="send" disabled={!text.trim() || busy} aria-label="Send">
                <Icon name="chat" size={18} />
              </button>
            </form>
          </section>
        ) : (
          <Card>
            <Empty title={patientId ? 'Opening…' : 'Pick a conversation'}>{patientId ? '' : 'Choose a patient on the left, or start a conversation with any patient from the menu above.'}</Empty>
          </Card>
        )}
      </div>
    </>
  );
}
